import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchAllUsers, UserSuggestion } from '../services/profileService.ts';

export const useProfileSearch = () => {
    const historyHook = useHistory();
    const [searchTerm, setSearchTerm] = useState('');
    const [allUsers, setAllUsers] = useState<UserSuggestion[]>([]); // Cache for suggestions
    const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false); // Control suggestion visibility
    const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(true);

    // Fetch all users once on mount
    useEffect(() => {
        let isMounted = true;
        setIsSuggestionsLoading(true);
        fetchAllUsers()
            .then(data => {
                if (isMounted) {
                    setAllUsers(data);
                }
            })
            .catch(error => {
                console.error("useProfileSearch: Failed to fetch all users", error);
                if (isMounted) {
                    setAllUsers([]); // Ensure it's an empty array on error
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsSuggestionsLoading(false);
                }
            });

        return () => { isMounted = false; }; // Cleanup function
    }, []);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);

        if (value.trim() === '') {
            setSuggestions([]);
            setShowSuggestions(false);
        } else {
            const lowerCaseValue = value.toLowerCase();
            // Ensure allUsers is an array before filtering
            const filtered = (allUsers || []).filter(user =>
                user.username.toLowerCase().includes(lowerCaseValue) ||
                user.walletAddress.toLowerCase().includes(lowerCaseValue)
            ).slice(0, 10); // Limit suggestions
            setSuggestions(filtered);
            setShowSuggestions(true);
        }
    }, [allUsers]); // Dependency on allUsers

    const handleSuggestionClick = useCallback((address: string) => {
        setSearchTerm(''); // Clear search term
        setSuggestions([]);
        setShowSuggestions(false);
        historyHook.push(`/profile?address=${address}`); // Navigate
    }, [historyHook]);

    const handleSearchSubmit = useCallback(() => {
        if (searchTerm.trim()) {
            setShowSuggestions(false);
            historyHook.push(`/profile?address=${encodeURIComponent(searchTerm.trim())}`);
            // Keep searchTerm if navigation might fail or user wants to see what they searched for
            // setSearchTerm('');
        }
    }, [searchTerm, historyHook]);

    const handleSearchKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleSearchSubmit();
        }
    }, [handleSearchSubmit]);

    const hideSuggestions = useCallback(() => {
        // Delay hide to allow click on suggestions
        setTimeout(() => setShowSuggestions(false), 150);
    }, []);

    const showSuggestionsOnFocus = useCallback(() => {
         setShowSuggestions(searchTerm.trim() !== '' && suggestions.length > 0);
    }, [searchTerm, suggestions]);


    return {
        searchTerm,
        suggestions,
        showSuggestions,
        isSuggestionsLoading,
        handleSearchChange,
        handleSuggestionClick,
        handleSearchSubmit,
        handleSearchKeyPress,
        hideSuggestions,
        showSuggestionsOnFocus,
    };
};
